/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef} from 'ag-grid-community';
import {PageTitle} from '@ion/applications/src/pages/admin/AdminContainer';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {DropdownArrowIcon} from '@ion/dls/src/icons/DropdownArrowIcon';
import {DropdownReverseArrowIcon} from '@ion/dls/src/icons/DropdownReverseArrowIcon';
import SaveColumnEditor from './SaveColumnEditor';
import SavedColumnList from './SavedColumnList';
import {COLUMN_NAME_MAX_LENGTH} from './types';
import useSavedColumnList from './useSavedColumnList';
import useUserColumnViews from './useUserColumnViews';
import {
  checkIsDefaultOrSelectAllView,
  getCurrentColumnView,
  getSavedColumnAndCurrentColumn
} from './utils';

export type SavedColumnViewsProps = {
  columns?: ColDef[];
  defaults?: ColDef[];
  checkIsDeleteDialogOpen?: (isOpen?: boolean) => void;
  onSelectColumnList?: (selectedColumns: string) => void;
  getUpdatedColumnView?: (viewName: string, updatedColumns: string) => void;
  setIsSavedColumnChanged?: Function;
  onReset?: Function;
  isDefaultOrAllColumn?: boolean;
  isCheckBoxUpdated?: boolean;
};

const useStyles = makeStyles(() => {
  return {
    button : {
      fontSize : 12
    },
    buttonWidth : {
      minWidth : '176px'
    }
  };
});

/**
 * @SavedColumnContainer
 *
 * The SavedColumnContainer provides a UI to show Save As and Saved Columns Tabs in Column Chooser
 * Component in the Device Grid, which allows the customer to save multiple column view.
 * This only renders if hasMultiColumnPreferences is set to true.
 *
 * ```typescript
 *
 *    <section>
 *      {hasMultiColumnPreferences && (
 *        <SavedColumnContainer columns={updateColumn} />
 *      )}
 *    </section>
 * ```
 *
 * @see ColumnChooser
 */
export const SavedColumnContainer = ({
  columns,
  defaults,
  checkIsDeleteDialogOpen,
  onSelectColumnList,
  getUpdatedColumnView,
  setIsSavedColumnChanged,
  onReset,
  isDefaultOrAllColumn = false,
  isCheckBoxUpdated = false
}: SavedColumnViewsProps) => {
  const classes                                               = useStyles();
  const pageName                                              = PageTitle.devices;
  const [saveAsAnchorElement, setSaveAsAnchorElement]         = useState<HTMLButtonElement>();
  const [columnListAnchorElement, setColumnListAnchorElement] = useState<HTMLButtonElement>();
  const [isOpenSaveColumnEditor, setIsOpenSaveColumnEditor]   = useState(false);
  const [isOpenSavedColumnList, setIsOpenSavedColumnList]     = useState(false);
  const [isListLoading, setIsListLoading]                     = useState(false);

  const {isSaveAsDisabled, selectedColumns} = useUserColumnViews(columns);
  const {
    savedColumnViews,
    selectedColumnName,
    isNotAllowToCreate,
    updateSavedColumns,
    getSavedColumnList,
    removeSavedColumnViews
  } = useSavedColumnList({pageName});

  /**
   * If Default Column or Select All is selected then unselect the selected Saved Column list
   * and disable the Save As button.
   * When isCheckBoxUpdated is true then enable the Save As button.
   */
  useEffect(() => {
    if (isDefaultOrAllColumn) {
      updateSavedColumns('', true);
    } else if (isCheckBoxUpdated) {
      updateSavedColumns(selectedColumnName, false);
    }
  }, [isDefaultOrAllColumn, isCheckBoxUpdated, updateSavedColumns]);

  /**
   * Run only at first render or on savedColumnViews change.
   * To show the current selected saved column view name.
   * And also check if it is a Default or select all column view ...
   */
  useEffect(() => {
    if (savedColumnViews?.length) {
      const isDefaultOrSelectAllView = checkIsDefaultOrSelectAllView(defaults, columns, selectedColumns);
      const {
        selectedSavedViewColumn,
        currentColumn
      } = getSavedColumnAndCurrentColumn(savedColumnViews, selectedColumnName, selectedColumns);
      if (isNotAllowToCreate && !isDefaultOrSelectAllView) {
        updateSavedColumns('', false);
      } else if (!isNotAllowToCreate && isDefaultOrSelectAllView) {
        updateSavedColumns('', true);
      } else if (selectedColumnName && selectedSavedViewColumn !== currentColumn) {
        const currentColumnView = getCurrentColumnView(savedColumnViews, currentColumn);
        updateSavedColumns(currentColumnView?.viewName || '', false);
      }
    }
  }, [savedColumnViews]);

  /**
   * Compare selected saved column view's column and current checked columns. And pass
   * selected column name and current checked column as arguments to getUpdatedColumnView method ...
   */
  useEffect(() => {
    if (savedColumnViews?.length) {
      const {
        selectedSavedViewColumn,
        currentColumn,
        currentColumnWithWidth
      } = getSavedColumnAndCurrentColumn(savedColumnViews, selectedColumnName, selectedColumns);
      setIsSavedColumnChanged(selectedSavedViewColumn !== currentColumn);
      getUpdatedColumnView(selectedColumnName, currentColumnWithWidth);
    }
  }, [selectedColumnName, selectedColumns, savedColumnViews, getSavedColumnAndCurrentColumn]);

  /**
   * Invoked when the user chooses to click on Save As TertiaryButton, and it will
   * open the SaveColumnEditor dialog.
   */
  const onSaveAsClick = useCallback(function(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
    setIsOpenSaveColumnEditor(true);
    setSaveAsAnchorElement(e?.currentTarget);
  }, []);

  /**
   * Invoked when the user chooses to click on Cancel button in SaveColumnEditor and
   * it will close the dialog.
   */
  const onCloseSaveColumnEditor = useCallback(function() {
    setIsOpenSaveColumnEditor(false);
    setSaveAsAnchorElement(null);
  }, []);

  /**
   * This closes the SaveColumnEditor dialog when no columns are selected.
   */
  useEffect(() => {
    if (isSaveAsDisabled) {
      onCloseSaveColumnEditor();
    }
  }, [isSaveAsDisabled]);

  /**
   * Invoked when the user chooses to click on Saved Columns TertiaryButton, and it will
   * open the SavedColumnList dropdown dialog.
   */
  const onSavedColumnsClick = useCallback(function(
    e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
    onCloseSaveColumnEditor();
    setIsOpenSavedColumnList(true);
    setColumnListAnchorElement(e?.currentTarget);
  }, []);

  /**
   * Invoked when the user chooses to click on outside of Saved Columns dropdown and
   * it will close the dialog.
   */
  const onCloseSavedColumnList = useCallback(function() {
    setIsOpenSavedColumnList(false);
    setColumnListAnchorElement(null);
  }, []);

  /**
   * @checkLoadingStatus pass as a callback function in SaveColumnEditor component
   * to get the loading status when create and save a new column view.
   */
  const checkLoadingStatus = useCallback(function(isLoading = false): void {
    setIsListLoading(isLoading);
  }, []);

  const isValidColumnName = selectedColumnName?.length > COLUMN_NAME_MAX_LENGTH;

  return (
    <>
      <TertiaryButton disabled={isSaveAsDisabled || isNotAllowToCreate}
        className={classes.button}
        onClick={onSaveAsClick}>
        {translate('common:columnChooser.saveAs')}
      </TertiaryButton>
      <TertiaryButton disabled={savedColumnViews?.length < 1}
        className={`${classes.button} ${classes.buttonWidth}`}
        onClick={onSavedColumnsClick}
        endIcon={!isOpenSavedColumnList ? <DropdownArrowIcon /> : <DropdownReverseArrowIcon />} >
        {selectedColumnName
          ? <Tooltip arrow
            title={isValidColumnName ? selectedColumnName : ''}>
            <span>{isValidColumnName ? `${selectedColumnName?.substring(0, 13)}...` : selectedColumnName}</span>
          </Tooltip>
          : translate('common:columnChooser.savedColumns') }
      </TertiaryButton>
      <SaveColumnEditor isOpen={isOpenSaveColumnEditor}
        anchorEl={saveAsAnchorElement}
        selectedColumns={selectedColumns}
        pageName={pageName}
        checkLoadingStatus={checkLoadingStatus}
        getSavedColumnList={getSavedColumnList}
        onClose={onCloseSaveColumnEditor} />
      {isOpenSavedColumnList && <SavedColumnList isOpen={isOpenSavedColumnList}
        anchorEl={columnListAnchorElement}
        isListLoading={isListLoading}
        savedColumnViews={savedColumnViews}
        selectedColumnName={selectedColumnName}
        updateSavedColumns={updateSavedColumns}
        removeSavedColumnViews={removeSavedColumnViews}
        onSelectColumnList={onSelectColumnList}
        onReset={onReset}
        checkIsDeleteDialogOpen={checkIsDeleteDialogOpen}
        onClose={onCloseSavedColumnList} />}
    </>
  );
};

export default SavedColumnContainer;
